import { useCallback, useEffect, useState } from 'react';

/**
 * Ricorda che il TD ha chiuso l'avviso "il giorno eccede la richiesta di viaggio"
 * per un dato itinerario. È una preferenza di sola presentazione: vive in
 * localStorage (per browser, non per utente) e non tocca il dato persistito.
 *
 * Il valore memorizzato non è un semplice flag ma la firma della finestra della
 * travel request: se il cliente/TD cambia le date della richiesta, la firma non
 * combacia più e l'avviso torna a farsi vedere invece di restare zittito per sempre.
 */
const STORAGE_PREFIX = 'p2b:builder:exceeds-warning-dismissed:';
// Sidebar e step giorno sono fratelli: senza un segnale comune, chiudere l'avviso
// nell'uno lascerebbe il rosso acceso nell'altro fino al reload.
const CHANGE_EVENT = 'p2b:exceeds-warning-dismissal-changed';

const storageKey = (itineraryId: string) => `${STORAGE_PREFIX}${itineraryId}`;

const windowSignature = (start?: string | null, end?: string | null) => `${start || ''}|${end || ''}`;

const readDismissal = (itineraryId?: string | null, signature?: string): boolean => {
  if (!itineraryId) return false;
  try {
    return localStorage.getItem(storageKey(itineraryId)) === signature;
  } catch {
    // Safari in navigazione privata / storage disabilitato: l'avviso resta visibile.
    return false;
  }
};

export function useExceedsWarningDismissal(
  itineraryId?: string | null,
  requestStart?: string | null,
  requestEnd?: string | null,
) {
  const signature = windowSignature(requestStart, requestEnd);
  const [dismissed, setDismissed] = useState(() => readDismissal(itineraryId, signature));

  useEffect(() => {
    setDismissed(readDismissal(itineraryId, signature));

    const sync = () => setDismissed(readDismissal(itineraryId, signature));
    window.addEventListener(CHANGE_EVENT, sync);
    // `storage` copre le altre tab aperte sullo stesso itinerario.
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [itineraryId, signature]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    if (!itineraryId) return;
    try {
      localStorage.setItem(storageKey(itineraryId), signature);
    } catch {
      // Senza storage la chiusura vale solo per la sessione corrente: accettabile.
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, [itineraryId, signature]);

  return { dismissed, dismiss };
}
